import { Asset } from '@innovyze/map-components';
import {
	AssetGrid,
	FilterCard,
	FilterProvider,
	GridMenuItems,
	InspectionScoredAttributes,
	PropertyPane,
	TabTypes,
	getMapLayerId,
	useFilter,
	Background,
	EditorModes,
	MapPosition,
	ReactMap,
	ReactMapProps,
} from '@innovyze/inno-map';
import {
	AssetDetails,
	InspDetails,
	TaskDetails,
} from '@innovyze/lib-am-common/Components';
import React, { useCallback, useMemo, useState } from 'react';
import StyledMap, { Popout, Popout2 } from './Map.styles';
import {
	configLayers,
	dataServicesMainMap,
	defaultInspectionSources,
	generalOptionsDefault,
	otherOptions,
	tileOptions,
} from '@innovyze/lib-am-common/Utils';
import {
	getMapLayers,
	getMapRiskLayers,
} from '@innovyze/lib-am-common/Actions';
import {
	getSubscriptions,
	isAdmin,
	useAuth0,
	useDatadogLogs,
	useEntitlements,
	useIsFeatureEnabled,
	useSettings,
} from '@innovyze/stylovyze';
import {
	selectMapRehabLayers,
	riskLayersSelector,
} from '@innovyze/lib-am-common/Selectors';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';

import { AssetPopup } from '@Components/AssetPopup';
import { InspectionStandard } from '@Types';
import { RiskLayersMask } from '@innovyze/lib-am-common/Types';
import { assetsBaseUrl } from '@Apis/assets.api';
import { fullAccess } from '@innovyze/shared-utils';
import { geojsonRestictions } from '@Utils/map.utils';
import { inspectionStandard } from '@Utils/inspections.utills';
import { calculateRadiusFromBounds } from './mapBounds.utils';
import { shouldIgnoreDefaultPosition } from './mapPosition.utils';
import { parseAssetDetailParams } from './mapAssetDetail.utils';
import { useDatagridFilter } from '@Hooks/useDatagridFilter';
import { useGlobalization } from '@Translations/useGlobalization';
import { SelectedAsset, useMapState } from '@Services/MapState';
import { usePolygonTool } from '@Hooks/usePolygonTool';
import { useReactMapConfig } from '@innovyze/stylovyze';
import {
	assetSchemaSelectorByType,
	getAllAssetsSchemaAction,
} from '@innovyze/lib_asset_schema_store';
import { useFeatureFlagBridge } from './useFeatureFlagBridge';

interface InspectionDetail {
	videoUrl: string;
	inspectionId: string;
	standard: InspectionStandard;
}

interface TaskDetail {
	taskId: string;
	taskType: string;
}

interface EventDetail {
	eventId: string;
	taskType: string;
}

interface AssetDetail {
	assetId: string;
	assetType: string;
	layerId?: string;
}

const useIsFeatureEnabledString = useIsFeatureEnabled as unknown as (
	feature: string,
) => boolean;

// eslint-disable-next-line @typescript-eslint/naming-convention
const MapPage = () => {
	const { t } = useGlobalization();
	const dispatch = useDispatch();
	const { token } = useAuth0();
	const { hasAccessToFeature } = useEntitlements();

	const featureFlagBridge = useFeatureFlagBridge();

	const iAmAdmin = isAdmin();
	const settings = useSettings();
	const assetTrace = useIsFeatureEnabledString('info-360-map-asset-trace');
	const cbaRangedValues = useIsFeatureEnabledString(
		'map-color-by-attribute-ranged-values',
	);
	const assetGrid = useIsFeatureEnabledString('map-asset-grid');
	const assetGridAssetFocus = useIsFeatureEnabledString(
		'i-360-release-139226-find-assets-on-map-via-data-grid',
	);
	const themeEditorMode = iAmAdmin
		? EditorModes.editAll
		: EditorModes.editOwn;

	const tiledInspections = useIsFeatureEnabledString(
		'info-360-map-tiled-inspections',
	);
	const tiledRisk = useIsFeatureEnabledString('info-360-map-tiled-risk');
	const isTilesV3Enabled = useIsFeatureEnabledString(
		'info-360-am-map-tiles-v-3',
	);
	const mapTooltip = useIsFeatureEnabledString(
		'info360-platform-map-tooltip',
	);
	const cloudOsTilesetFlagEnabled = useIsFeatureEnabledString(
		'i-360-tileset-read-from-cloudos',
	);
	// E2E runs against legacy tile intercepts/fixtures — never route to CloudOS in Cypress.
	const isCloudOsTilesetEnabled =
		typeof window !== 'undefined' &&
		'Cypress' in window &&
		(window as Window & { Cypress?: unknown }).Cypress
			? false
			: Boolean(cloudOsTilesetFlagEnabled);
	// See comments in package.md wrt eslint-disable-line below

	const showRehab =
		useIsFeatureEnabledString('info-360-map-rehab') &&
		hasAccessToFeature('I360A-REHAB');

	const subscriptions = getSubscriptions();
	const hasFullAccess = fullAccess(subscriptions);

	const [radius, setRadius] = useState<number>(
		geojsonRestictions.radiusDefault,
	);
	const [bbox, setBBox] = useState<number[]>([]);

	const [detailInspection, setDetailInspection] =
		useState<InspectionDetail | null>(null);

	const [detailTask, setDetailTask] = useState<TaskDetail | null>(null);
	const [detailEvent, setDetailEvent] = useState<EventDetail | null>(null);
	const [detailAsset, setDetailAsset] = useState<AssetDetail | null>(null);

	const [focusedAssets, setFocusedAssets] = useState<Asset[]>([]);
	const { setPolygonFilter, isPolygonToolEnabled } = usePolygonTool();
	const {
		datagridFilter,
		datagridFilterEndpoints,
		dataGridFilterCount = 0,
	} = useDatagridFilter();

	const { additionalFilterProps } = useFilter();

	const {
		position,
		setPosition,
		background,
		setBackground,
		selected,
		setSelected,
	} = useMapState();

	React.useEffect(() => {
		dispatch(getAllAssetsSchemaAction());
		if (showRehab) {
			dispatch(getMapLayers());
		} else {
			dispatch(getMapRiskLayers());
		}
	}, []);

	const onBoundsChange = (bounds: number[]) => {
		setRadius(calculateRadiusFromBounds(bounds));
		setBBox(bounds);
	};

	const hasPosition = position !== undefined;
	const onPositionChange = useCallback(
		(newPosition: MapPosition) => {
			// If the current position is undefined (e.g. a fresh browse to the map page),
			//   and the map is telling us that it is sitting at the default unspecified position (0,0 zoom 3)
			//   don't set the position (which actually tells the map to go to (and stay at) this position...)
			// This will allow data to set the boundary of the map automatically.
			if (shouldIgnoreDefaultPosition(hasPosition, newPosition)) {
				return;
			}

			setPosition(newPosition);
		},
		[hasPosition, setPosition],
	);

	const onBackgroundChanged = (newBackground: Background) => {
		setBackground(newBackground.key);
	};

	const onSelectedAssetsChange = (items: SelectedAsset[] | null) => {
		setSelected(items || []);
	};

	const mask = RiskLayersMask.Published;
	// Memoize the selector factory result so the same selector instance is reused across
	// renders — fixes broken memoization cache that caused continuous re-renders and OOM.
	// Use shallowEqual so identical filter results don't trigger unnecessary re-renders.
	const stableRiskLayersSelector = useMemo(
		() => riskLayersSelector(mask),
		[mask],
	);
	const riskLayers = useSelector(stableRiskLayersSelector, shallowEqual);
	const rehabLayers = useSelector(selectMapRehabLayers);

	// Memoize options to prevent recreation on every render
	// This prevents the PROPAGATE_FORGE_TOKEN_UPDATED feedback loop
	const options = useMemo(
		() => ({
			showRisk: true,
			riskLayersMask: mask,
			riskLayers,
			generalOptions: generalOptionsDefault,
			tilesSource: tileOptions,
			inspectionSources: defaultInspectionSources,
			otherSource: otherOptions,
			rehabLayers: showRehab ? rehabLayers : undefined,
			featureFlagBridge,
		}),
		[mask, riskLayers, rehabLayers, showRehab, featureFlagBridge],
	);

	// Would like to use version rather than index
	// but currently need to set propertyLookup to valid calls
	// on first dataServices load - as subsequent changes are
	// ignored in inno-map
	const riskGroupPublishLimit = 10;

	const dataServices = useMemo(
		() =>
			dataServicesMainMap(
				options,
				token,
				position,
				radius,
				bbox,
				riskGroupPublishLimit,
				tiledInspections,
				tiledRisk,
				isTilesV3Enabled,
				isCloudOsTilesetEnabled,
			),
		[
			token,
			position,
			radius,
			bbox,
			options,
			tiledInspections,
			tiledRisk,
			isTilesV3Enabled,
			isCloudOsTilesetEnabled,
		],
	);

	const layerConfigure = useMemo(
		() =>
			configLayers(
				token,
				options,
				[],
				tiledInspections,
				tiledRisk,
				isTilesV3Enabled,
				isCloudOsTilesetEnabled,
			),
		[
			token,
			options,
			tiledInspections,
			tiledRisk,
			isTilesV3Enabled,
			isCloudOsTilesetEnabled,
		],
	);

	const resetDetailInspection = useCallback(() => {
		setDetailInspection(null);
	}, []);

	const resetDetailTask = useCallback(() => {
		setDetailTask(null);
	}, []);

	const resetDetailEvent = useCallback(() => {
		setDetailEvent(null);
	}, []);

	const resetDetailAsset = useCallback(() => {
		setDetailAsset(null);
	}, []);

	const onClickAway = useCallback(() => {
		resetDetailInspection();
		resetDetailAsset();
	}, []);

	const onInspDetailClicked = useCallback(
		(
			videoUrl: string,
			inspectionId: string,
			standard: InspectionStandard,
		) => {
			setDetailInspection({ videoUrl, inspectionId, standard });
		},
		[],
	);

	const onTaskDetailClicked = useCallback(
		(taskId: string, standard: InspectionStandard) => {
			setDetailTask({ taskId, taskType: standard });
		},
		[],
	);

	const onEventDetailClicked = useCallback(
		(eventId: string, standard: InspectionStandard) => {
			setDetailEvent({ eventId, taskType: standard });
		},
		[],
	);

	const inspDetailActions = {
		onInspectionDetailClicked: onInspDetailClicked,
		onTaskDetailClicked: onTaskDetailClicked,
		onEventDetailClicked: onEventDetailClicked,
	};

	const onViewInspection = useCallback(
		(inspection: InspectionScoredAttributes) => {
			const videoUrl = inspection.videoUrl;
			const inspectionId = inspection._id;
			const standard = inspectionStandard(inspection.inspectionVersion);
			setDetailInspection({ videoUrl, inspectionId, standard });
		},
		[],
	);

	const onViewTask = useCallback((inspection: InspectionScoredAttributes) => {
		const taskId = inspection._id;
		const taskType = inspection.inspectionVersion;
		setDetailTask({ taskId, taskType });
	}, []);

	const onViewEvent = useCallback(
		(inspection: InspectionScoredAttributes) => {
			const eventId = inspection._id;
			const taskType = inspection.inspectionVersion;
			setDetailEvent({ eventId, taskType });
		},
		[],
	);

	const onViewAssetDetail = useCallback(
		(
			id: string,
			layerId?: string,
			assetId?: string,
			assetType?: string,
		) => {
			setDetailAsset(
				parseAssetDetailParams(id, layerId, assetId, assetType),
			);
		},
		[],
	);

	const searchEndpoints = useMemo(
		() => [
			{
				url: `${assetsBaseUrl}/assets?fuzzySearch={{query}}`,
				token: token,
				dataServiceId: 'tiles',
			},
		],
		[token],
	);

	const tracingEndpoint = useMemo(
		() =>
			assetTrace
				? {
						url: `${assetsBaseUrl}/assets/{{assetId}}/trace?assetType={{assetType}}`,
						token,
				  }
				: undefined,
		[token],
	);

	const shouldShowInspections = useSelector(state =>
		selected.some(asset =>
			assetSchemaSelectorByType(state, asset.assetType)?.hasCapability(
				'Inspections',
			),
		),
	);

	const gridActionItems = [];
	const excludeFilter = useIsFeatureEnabled(
		'i-360-release-145677-remove-asset-from-filter',
	);
	if (excludeFilter) {
		gridActionItems.push(GridMenuItems.exclude);
	}

	const reactMapConfig = useReactMapConfig();
	const ddLogger = useDatadogLogs();

	return (
		<StyledMap>
			{detailInspection && (
				<Popout2>
					<InspDetails
						// videoUrl={detailInspection.videoUrl}
						back={{
							action: resetDetailInspection,
							label: t('Back to Asset Details'),
						}}
						inspectionId={detailInspection.inspectionId}
						standard={detailInspection.standard}
					/>
				</Popout2>
			)}
			{detailTask && (
				<Popout2>
					<TaskDetails
						back={{
							action: resetDetailTask,
							label: t('Back to Asset Details'),
						}}
						taskType={detailTask.taskType}
						taskId={detailTask.taskId}
						type={'task'}
					/>
				</Popout2>
			)}
			{detailEvent && (
				<Popout2>
					<TaskDetails
						back={{
							action: resetDetailEvent,
							label: t('Back to Asset Details'),
						}}
						taskType={detailEvent.taskType}
						taskId={detailEvent.eventId}
						type={'event'}
					/>
				</Popout2>
			)}
			{detailAsset && (
				<Popout>
					<AssetDetails
						assetId={detailAsset.assetId}
						back={{ action: onClickAway, label: t('Back to map') }}
						assetType={detailAsset.assetType}
						actions={inspDetailActions}
						adminFullAccess={hasFullAccess}
						layerId={detailAsset.layerId}
					/>
				</Popout>
			)}
			<ReactMap
				featureFlagBridge={featureFlagBridge}
				config={reactMapConfig}
				dataServices={dataServices}
				layerConfig={layerConfigure}
				summaryPanel={
					<PropertyPane
						tabsToDisplay={[TabTypes.asset, TabTypes.spatial]}
						viewInspection={onViewInspection}
						viewAssetDetails={onViewAssetDetail}
						viewTask={onViewTask}
						viewEvent={onViewEvent}
						showInspections={shouldShowInspections}
					/>
				}
				position={position}
				background={background}
				onPositionChange={onPositionChange}
				onBoundsChange={onBoundsChange}
				onBackgroundChanged={onBackgroundChanged}
				selectedAssets={selected}
				onSelectedAssetsChange={onSelectedAssetsChange}
				searchEndpoints={searchEndpoints}
				tracingEndpoint={tracingEndpoint}
				themeEditorMode={themeEditorMode}
				hoverPopup={mapTooltip ? <AssetPopup /> : undefined}
				enableColorByAttributeRange={cbaRangedValues}
				datagridFilter={datagridFilter}
				datagridFilterEndpoints={datagridFilterEndpoints}
				focusedAssets={assetGridAssetFocus ? focusedAssets : undefined}
				zoomToFocusedAssets={assetGridAssetFocus}
				polygonControl={
					isPolygonToolEnabled && !!additionalFilterProps.assetType
				}
				onPolygonComplete={
					setPolygonFilter as ReactMapProps['onPolygonComplete']
				}
				assetGrid={
					assetGrid ? (
						<AssetGrid
							onViewDetails={({
								assetId,
								assetType,
								systemType,
							}: {
								assetId: string;
								assetType: string;
								systemType?: string;
							}) => {
								// Generate the layerId from the systemType
								const layerId = getMapLayerId(
									assetType,
									systemType ?? '',
								);
								setDetailAsset({ assetId, assetType, layerId });
							}}
							onSelectedAssetsChange={
								assetGridAssetFocus
									? setFocusedAssets
									: undefined
							}
							actionMenuItems={gridActionItems}
						/>
					) : undefined
				}
				logger={ddLogger.logger}
				logLevel="info"
				logContext={{
					tenantId: settings.companySettings?.organization,
				}}
			/>
			{dataGridFilterCount > 0 && (
				<FilterCard filterCount={dataGridFilterCount} />
			)}
		</StyledMap>
	);
};

export const Map = () => {
	return (
		<FilterProvider>
			<MapPage />
		</FilterProvider>
	);
};

export default Map;
